import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { CANDIDATE_REPORT_URL } from "../../redux/api/config";
import { useDispatch } from "react-redux";
import { setErrorMessage } from "../../redux/actions/appActions";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import ClientReportFilters from "./ClientReportFilters";
import { dateComparator } from "./sortComparators";
import { exportToExcel } from "./reportUtils";
import { buildClientReportPayload } from "../../redux/actions/b2bActions";
import React from "react";

interface CandidateReportData {
  user_id: number;
  name: string;
  email: string;
  dob: string;
  phone_number: string;
  registration_date: string;
  education_status: boolean;
  preference_status: boolean;
  experience_status: boolean;
  is_exam_completed: boolean;
  video_uploaded_status: boolean;
  video_approved_status: boolean;
  jobs_applied_count: number;
  is_profile_completed: boolean;
  /** Completion flag for specialization / specialisation step (API may use either spelling). */
  specialization_status?: boolean;
  specialisation_status?: boolean;
  id?: string;
}

const CandidateReport = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<CandidateReportData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 50,
  });
  const abortControllerRef = React.useRef<AbortController | null>(null);
  const skipNextEffectRef = React.useRef(false);

  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [selectedMonths, setSelectedMonths] = useState<number[]>([]);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  useEffect(() => {
    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }
    if (hasSubmitted) {
      fetchCandidateReport(paginationModel.page, paginationModel.pageSize);
    }
  }, [hasSubmitted, paginationModel.page, paginationModel.pageSize]);

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      flex: 1.2,
      minWidth: 130,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1.5,
      minWidth: 160,
      headerAlign: "center",
      align: "left",
    },
    {
      field: "dob",
      headerName: "DOB",
      flex: 1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      sortComparator: dateComparator,
    },
    {
      field: "phone_number",
      headerName: "Phone Number",
      flex: 1.1,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "registration_date",
      headerName: "Registration Date",
      flex: 1.2,
      minWidth: 140,
      headerAlign: "center",
      align: "center",
      sortComparator: dateComparator,
    },
    {
      field: "preference_status",
      headerName: "Preferences",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "is_profile_completed",
      headerName: "Profile",
      flex: 0.9,
      minWidth: 130,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "education_status",
      headerName: "Education",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "experience_status",
      headerName: "Experience",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "specialization_status",
      headerName: "Specialisation",
      flex: 0.9,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "is_exam_completed",
      headerName: "Psychometrics",
      flex: 0.9,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "video_uploaded_status",
      headerName: "Video Uploaded",
      flex: 0.9,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "video_approved_status",
      headerName: "Videos Approved",
      flex: 0.9,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      type: "boolean",
      renderCell: (params) => (
        <Typography sx={{ color: params.value ? "#4CAF50" : "#f44336", fontWeight: 600 }}>
          {params.value ? "✓" : "✗"}
        </Typography>
      ),
    },
    {
      field: "jobs_applied_count",
      headerName: "Jobs Applied",
      flex: 0.9,
      minWidth: 110,
      headerAlign: "center",
      align: "center",
      type: "number",
    },
  ];

  const fetchCandidateReport = async (
    page: number,
    fetchSize: number,
    filtersOverride?: { startDate: string | null; endDate: string | null; year: string; months: number[] }
  ) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const useStart = filtersOverride?.startDate ?? startDate;
    const useEnd = filtersOverride?.endDate ?? endDate;
    const useYear = filtersOverride?.year ?? selectedYear;
    const useMonths = filtersOverride?.months ?? selectedMonths;

    setLoading(true);
    setData([]);
    try {
      const payload = buildClientReportPayload(
        {
          startDate: useStart,
          endDate: useEnd,
          year: useYear,
          months: useMonths.length > 0 ? useMonths : undefined,
        },
        false,
        page,
        fetchSize
      );

      const response = await API_MIDDLEWARE(
        CANDIDATE_REPORT_URL,
        "POST",
        { "Content-Type": "application/json" },
        payload,
        undefined,
        true,
        true,
        abortControllerRef.current.signal
      );

      const { records, totalRecords } = response?.result || {};
      
      // Add unique IDs to each row for DataGrid
      const dataWithIds =
        records?.map((record: CandidateReportData, index: number) => ({
          ...record,
          specialization_status:
            record.specialization_status ?? record.specialisation_status,
          id: `${record.user_id}-${index}`,
        })) || [];

      setLoading(false);

      setData(dataWithIds);
      
      setTotalRecords(totalRecords || 0);
    } catch (err: any) {
      // Don't show error for aborted requests
      if (err.code === 20 || err.name === 'AbortError') {
        console.log('Request was cancelled');
        return;
      }
      if (err.code !== 401) {
        setLoading(false);
        dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      }
    } 
  };

  const handleSubmit = () => {
    skipNextEffectRef.current = true;
    setHasSubmitted(true);
    setPaginationModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
    fetchCandidateReport(0, paginationModel.pageSize, {
      startDate,
      endDate,
      year: selectedYear,
      months: selectedMonths,
    });
  };

  const handleClearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedYear("");
    setSelectedMonths([]);
    setHasSubmitted(false);
    setTotalRecords(0);
    setData([]);
    setPaginationModel({ page: 0, pageSize: 50 });
  };

  const handleDownloadExcel = async () => {
    if (data.length === 0) return;

    setDownloadingExcel(true);
    try {
      const payload = buildClientReportPayload(
        {
          startDate,
          endDate,
          year: selectedYear,
          months: selectedMonths.length > 0 ? selectedMonths : undefined,
        },
        true
      );

      const response = await API_MIDDLEWARE(
        CANDIDATE_REPORT_URL,
        "POST",
        { "Content-Type": "application/json" },
        payload,
        undefined,
        true,
        true
      );

      const { records } = response?.result || {};

      if (!records || records.length === 0) {
        dispatch(setErrorMessage("No data available to download"));
        setDownloadingExcel(false);
        return;
      }

      const exportData = records.map((row: CandidateReportData) => {
        const specializationDone =
          row.specialization_status ?? row.specialisation_status ?? false;
        return {
          Name: row.name,
          Email: row.email,
          DOB: row.dob,
          "Phone Number": row.phone_number,
          "Registration Date": row.registration_date,
          Preferences: row.preference_status ? "Yes" : "No",
          Profile: row.is_profile_completed ? "Yes" : "No",
          Education: row.education_status ? "Yes" : "No",
          Experience: row.experience_status ? "Yes" : "No",
          Specialisation: specializationDone ? "Yes" : "No",
          Psychometrics: row.is_exam_completed ? "Yes" : "No",
          "Video Uploaded": row.video_uploaded_status ? "Yes" : "No",
          "Videos Approved": row.video_approved_status ? "Yes" : "No",
          "Jobs Applied": row.jobs_applied_count,
        };
      });

      exportToExcel({
        data: exportData,
        filename: "Candidate_Report",
        sheetName: "Candidate Report",
        maxColumnWidth: 25,
      });

      setDownloadingExcel(false);
    } catch (err: any) {
      if (err.code !== 401) {
        dispatch(setErrorMessage(err?.errorMsg ?? APP_CUSTOM_ERROR));
      }
      setDownloadingExcel(false);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden", boxSizing: "border-box" }}>
      {/* Header with Title and Download Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          gap: 2,
          pr: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "600",
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          Candidate Report
        </Typography>
        <Button
          startIcon={!downloadingExcel ? <DownloadIcon /> : undefined}
          variant="contained"
          disabled={data.length === 0 || loading || downloadingExcel}
          onClick={handleDownloadExcel}
          sx={{
            backgroundColor: "#756DE9",
            color: "#fff",
            borderRadius: "100px",
            padding: "10px 20px",
            "&:hover": {
              backgroundColor: "#5a4dbf",
            },
            "&:disabled": {
              backgroundColor: "#E0E0E0",
              color: "#999999",
            },
            textTransform: "capitalize",
            fontSize: "0.95rem",
            whiteSpace: "nowrap",
            flexShrink: 0,
            minWidth: "fit-content",
          }}
        >
          {downloadingExcel ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} sx={{ color: "#fff" }} />
              Downloading...
            </Box>
          ) : (
            "Download Excel"
          )}
        </Button>
      </Box>

      {/* Filters Section */}
      <ClientReportFilters
        startDate={startDate}
        endDate={endDate}
        selectedYear={selectedYear}
        selectedMonths={selectedMonths}
        hasSubmitted={hasSubmitted}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onYearChange={setSelectedYear}
        onMonthsChange={setSelectedMonths}
        onSubmit={handleSubmit}
        onClearFilters={handleClearFilters}
      />

      {/* Data Grid */}
      {hasSubmitted ? (
        <Box
          sx={{
            height: 600,
            width: "100%",
            maxWidth: "100%",
            overflow: "auto",
            pr: 2,
            "& .MuiDataGrid-root": {
              border: "1px solid #E0E0E0",
              borderRadius: "8px",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#756DE9 !important",
              color: "#fff !important",
              fontSize: "14px",
              fontWeight: "700",
              minHeight: "56px !important",
              maxHeight: "56px !important",
              lineHeight: "56px !important",
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "700",
                color: "#fff !important",
                overflow: "visible",
                whiteSpace: "normal",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#756DE9 !important",
              },
              "& .MuiDataGrid-iconSeparator": {
                color: "rgba(255, 255, 255, 0.5)",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "#fff !important",
                opacity: 1,
              },
              "& .MuiDataGrid-menuIcon": {
                color: "#fff !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "#fff !important",
              },
            },
            "& .MuiDataGrid-cell": {
              fontSize: "14px",
              borderBottom: "1px solid #F0F0F0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#F5F5FF",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #E0E0E0",
              backgroundColor: "#F8F8F8",
            },
          }}
        >
          <DataGrid
            rows={data}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={totalRecords}
            paginationMode="server"
            loading={loading}
            disableRowSelectionOnClick
            slots={{
              noRowsOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Typography sx={{ color: "#999", fontSize: "16px" }}>
                    No data available
                  </Typography>
                </Box>
              ),
              loadingOverlay: () => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress size={40} sx={{ color: "#756DE9" }} />
                </Box>
              ),
            }}
            sx={{
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "8px",
                height: "8px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
                background: "#F5F5F5",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                background: "#C1C1C1",
                borderRadius: "4px",
                "&:hover": {
                  background: "#A8A8A8",
                },
              },
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            height: 600,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#F8F8F8",
            borderRadius: "8px",
            border: "1px solid #E0E0E0",
          }}
        >
          <Typography sx={{ color: "#999", fontSize: "16px" }}>
            Use filters above (custom date range or year / month) and click Apply to view candidate report
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CandidateReport;
